import axios from 'axios';
import bulkActions from '@/Contacts/store/bulk-actions';
import contactFilters from '@/Contacts/store/contact-filters';
import { generateSlippingAwayConditions } from '@/Contacts/utilities';
import customErrors from '@/Core/config/errors';
import { PreferenceKeys } from '@/Core/config/preferences';
import { generatePaginationModule } from '@/Core/store/modules/pagination';
import { debounce, transformDateToOldFormat } from '@/Core/utilities';
import { loadFromLocalStorage, loadHeaders } from '@/Core/utilities/tables';
import formatContactName from '@/Core/filters/formatContactName';
import { feathers } from '@/feathers-client';
import isEqual from 'lodash.isequal';
import fieldTypes from '../components/import/config/contact-fields-types';
import contactTableHeaders, {
  EMAIL_PRIMARY_FIELD,
  EMAIL_SECONDARY_FIELD,
  specialSortColumns,
  SPOUSE_EMAIL_FIELD,
} from '../config/table-headers';
import { ESSearchHeader } from '@/Core/config/es-search-header';
import { getNextCampaignActionDate } from '@/Campaigns/utilities';
import { formatPhoneNumberOnlyDigits } from '@/Core/utilities/formatPhoneNumberOnlyDigits';
import {
  ES_STATUS
} from '@/Contacts/config/profile-optin-status'

const DEFAULT_LIMIT_CONTACTS = 500;
const DEFAULT_LIMIT_EMAIL_LIST = 50;
const MAX_ES_SEARCH_PAGES_LIMIT = 20;

// Constants related to displaying custom fields
const CUSTOM_FIELD_PREFIX = 'custom_field';
const CUSTOM_FIELD_DELIMITER = '_';

const FEATURE_FLAG_CONTACTS_MS = 'contacts-ms';

let getUserPoll;

const initialState = {
  allBulkContacts: [],
  contacts: [],
  error: null,
  successMessage: null,
  isSuccessfullyDeleted: false,
  isLoading: null,
  shouldShowTeamMembersContacts: false,
  numberOfPages: null,
  searchKeywords: '',
  headers: contactTableHeaders,
  customFields: [],
  customFieldsTotal: 0,
  emailLists: [],
  hasMoreEmailListsToLoad: true,
  selectedEmailListId: null,
  isCustomAudienceCreated: false,
  isShownCreateCustomAudienceFb: false,
  pageLimitMessage: null,
  shouldReloadDashboard: false,
  stormDialerToken: null,
  stormUserCreated: false,
  dialerVisible: false,
  isStaticListLoading: false,
  shouldReloadContactsTable: false,
  selectedActionType: null,
  excludedContactsIds: [],
  visibleBulkContacts: [],
};

const contactsTablePagination = generatePaginationModule({
  findAction: 'contactsTable/findContacts',
  specialSortColumns,
});

const userHasContactsMSFlag = user => {
  return user.feature_flags.includes(FEATURE_FLAG_CONTACTS_MS);
};

// We need this check because Contacts V1 sends the custom_fields
// as a string and Contacts V2 sends it as an Array
const isCustomField = (customFields, item) => {
  const includesCustomFieldPrefix = item.value.includes(CUSTOM_FIELD_PREFIX);
  if (typeof customFields === 'string') {
    return customFields && includesCustomFieldPrefix;
  }

  return customFields && customFields.length && includesCustomFieldPrefix;
};

const createStormDialerUser = async user => {
  const { id, email, first_name: firstName, last_name: lastName } = user;
  return feathers.service('/storm-dialer/users').create({
    id,
    email,
    firstName,
    lastName,
    subscriptions: {
      multi: true,
    },
  });
};

// load stormapp
const loadStormScript = async () => {
  const stormScript = document.createElement('script');
  const stormSource = `https://app.wavv.com/js/dialer-2.5.0.js`;
  stormScript.setAttribute('src', stormSource);
  stormScript.setAttribute('async', true);
  document.head.appendChild(stormScript);
  await new Promise(resolve => {
    stormScript.addEventListener('load', () => {
      resolve();
    });
  });
};

/*
 * The contacts table module
 */
export default {
  namespaced: true,
  modules: {
    pagination: contactsTablePagination,
    contactFilters,
    bulkActions,
  },
  state: { ...initialState },
  getters: {
    // used in the contacts table to conditionally display fields
    // each field object will contain the actual field value
    // and column 'pinned' status to be filtered by rendering component
    contacts(state, getters) {
      if (state.contacts) {
        return state.contacts.map(contact => {
          return Object.assign(
            {},
            ...getters.visibleFields.map(item => {
              if (item.type === 'address') {
                const addressField =
                  contact.addresses &&
                  contact.addresses.find(address => address.address_type === item.address_type);
                return {
                  [item.text]: {
                    value: (addressField && addressField[item.value]) || '',
                    isCustomField: false,
                    dataType: item.type,
                    pinned: item.pinned || false,
                  },
                };
              }
              if (item.type === 'date') {
                return {
                  [item.text]: {
                    value: transformDateToOldFormat(contact[item.value]),
                    isCustomField: false,
                    dataType: item.type,
                    pinned: item.pinned || false,
                  },
                };
              }
              // display each visible custom field as a separate attribute
              if (isCustomField(contact.custom_fields, item)) {
                const nameArray = item.value.split(CUSTOM_FIELD_DELIMITER);
                const lastIndex = nameArray.length - 1;
                const customFieldId = parseInt(nameArray[lastIndex], 10);
                const customField = contact.custom_fields.filter(field => {
                  // .id is for Contacts V1 / .idUserCustomerType is for Contacts V2
                  const id = field.id || field.idUserCustomerType;
                  return id === customFieldId;
                });
                // .value is for Contacts V1 / .txtUserCustomerValue is for Contacts V2

                if (customField.length) {
                  const value = customField[0].value || customField[0].txtUserCustomerValue;
                  return {
                    [item.value]: {
                      value,
                      isCustomField: true,
                      dataType: customField[0].data_type || customField[0].dataType,
                      pinned: item.pinned || false,
                    },
                  };
                }
                return {
                  [item.value]: {
                    value: null,
                    isCustomField: true,
                    dataType: null,
                    pinned: item.pinned || false,
                  },
                };
              }
              return {
                [item.value]: {
                  value: contact[item.value],
                  isCustomField: false,
                  pinned: item.pinned || false,
                },
              };
            }),
            // special "hidden" field needed to maintain row uniqueness
            // even if all columns are hidden by user on customization
            { _key: contact.id, assigned_user_id: contact.assigned_user_id },
          );
        });
      }

      return [];
    },
    queryParams(state, getters) {
      return {
        ...getters['pagination/queryParams'],
        ...getters['contactFilters/filterQueries'],
        $teams: state.shouldShowTeamMembersContacts,
        ...(state.searchKeywords ? { $keywords: state.searchKeywords.trim() } : {}),
      };
    },
    queryParamsForAllContacts(state, getters) {
      const query = { ...getters.queryParams };
      delete query.$limit;
      delete query.$skip;
      query.$paginate = 'false';

      return query;
    },
    headers(state) {
      return JSON.parse(JSON.stringify(state.headers));
    },
    visibleFields(state) {
      return state.headers.filter(header => header.visible === true);
    },
    pinnedFields(state) {
      return state.headers.filter(header => header.pinned === true);
    },
    unpinnedFields(state) {
      return state.headers.filter(header => {
        return !header.pinned && header.visible;
      });
    },
    dateFields(getters) {
      return getters.headers.filter(header => header.type === 'date');
    },
    typeMappings(state) {
      const typeMappings = {};
      state.headers.forEach(header => {
        const isEmailField =
          header.value === EMAIL_PRIMARY_FIELD ||
          header.value === EMAIL_SECONDARY_FIELD ||
          header.value === SPOUSE_EMAIL_FIELD;
        if (isEmailField) {
          typeMappings[header.value] = header.type || 'email';
        } else {
          typeMappings[header.value] = header.type || 'text';
        }
      });
      return typeMappings;
    },
    customFieldsWithTypes(state) {
      return state.customFields.map(customField => {
        customField.type = fieldTypes.CUSTOM_FIELD;
        return customField;
      });
    },
    userEmailLists(state) {
      return state.emailLists;
    },
    hasEmaillists(state) {
      return state.emailLists.length > 0;
    },
    emailLists(state) {
      const { emailLists } = state;
      const emailListsCopy = emailLists.slice();
      return emailListsCopy.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
  },

  mutations: {
    appendExcludedContactId(state, excludedContactsIds) {
      state.excludedContactsIds.push(excludedContactsIds);
    },
    removeIdFromExcludedContactsIds(state, id) {
      let excludedContactsIdsSet = new Set(state.excludedContactsIds);
      excludedContactsIdsSet.delete(id);
      state.excludedContactsIds = [...excludedContactsIdsSet];
    },
    clearExcludedContactsIds(state) {
      state.excludedContactsIds = [];
    },
    setSelectedActionType(state, type) {
      state.selectedActionType = type;
    },
    setContacts(state, contacts) {
      state.contacts = contacts;
    },
    setAllBulkContacts(state, contacts) {
      state.allBulkContacts = contacts.data || contacts;
    },
    resetAllBulkContacts(state) {
      state.allBulkContacts = [];
    },
    setContactTotals(state, payload) {
      state.totals = payload;
    },
    setContactTableError(state, payload) {
      state.error = payload;
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    updateHeaders(state, headers) {
      state.headers = headers;
    },
    setCustomFieldsData(state, customFields) {
      state.customFields = state.customFields.concat(customFields);
    },

    resetCustomFields(state) {
      state.customFields = [];
    },

    setShouldShowTeamMembersContacts(state, shouldShowTeamMembersContacts) {
      state.shouldShowTeamMembersContacts = shouldShowTeamMembersContacts;
    },

    setNumberOfPages(state, numberOfPages) {
      state.numberOfPages = numberOfPages;
    },

    setPageLimitMessage(state, pageLimitMessage) {
      state.pageLimitMessage = pageLimitMessage;
    },

    setSearchKeywords(state, searchKeywords) {
      state.searchKeywords = searchKeywords;
    },
    setEmailLists(state, emailLists) {
      state.emailLists = emailLists;
    },
    appendEmailLists(state, emailLists) {
      state.emailLists = [...state.emailLists, ...emailLists];
    },
    setHasMoreEmailListsToLoad(state, hasMoreEmailListsToLoad) {
      state.hasMoreEmailListsToLoad = hasMoreEmailListsToLoad;
    },
    setHasEmailLists(state, value) {
      state.hasEmaillists = value;
    },
    setEmailListId(state, id) {
      state.selectedEmailListId = id;
    },
    setIsCustomAudienceCreated(state, boolean) {
      state.isCustomAudienceCreated = boolean;
    },
    setSuccessMessage(state, value) {
      state.successMessage = value;
    },
    setSuccessfulDelete(state, boolean) {
      state.isSuccessfullyDeleted = boolean;
    },
    setCreateCustomAudienceFb(state, boolean) {
      state.isShownCreateCustomAudienceFb = boolean;
    },
    setReloadDashboard(state, boolean) {
      state.shouldReloadDashboard = boolean;
    },
    setStormDialerToken(state, token) {
      state.stormDialerToken = token;
    },
    stormUserCreated(state) {
      state.stormUserCreated = true;
    },
    dialerVisible(state, visible) {
      state.dialerVisible = visible;
    },
    setIsStaticListLoading(state, isLoading) {
      state.isStaticListLoading = isLoading;
    },
    setShouldReloadContactsTable(state, shouldReload) {
      state.shouldReloadContactsTable = shouldReload;
    },
    setVisibleBulkContacts(state, payload) {
      const contacts = payload.map(contact => {
        return {
          id: contact.id,
          name: formatContactName(contact),
        };
      });
      state.visibleBulkContacts = contacts;
    },
  },

  actions: {
    /**
     * the v-toggle which calls this action sends true or null.
     * We explicitly cast to true/false
     */
    setShouldShowTeamMembersContacts({ state, commit, dispatch }, shouldShowTeamMembersContacts) {
      commit('setShouldShowTeamMembersContacts', !!shouldShowTeamMembersContacts);
      if (state.selectedEmailListId) {
        dispatch('findEmailListContacts');
      } else {
        dispatch('pagination/setPage', 1);
        dispatch('findContacts');
      }
    },

    setNumberOfPages({ state, commit, rootState }, numberOfPages) {
      commit('setPageLimitMessage', null);
      const { user } = rootState.userInformation;
      if (!user) return;

      const includesContactMS = userHasContactsMSFlag(user);
      if (includesContactMS && numberOfPages > MAX_ES_SEARCH_PAGES_LIMIT) {
        commit('setNumberOfPages', MAX_ES_SEARCH_PAGES_LIMIT);
        return;
      }

      commit('setNumberOfPages', numberOfPages);
    },

    setSearchKeywords({ commit, dispatch }, searchKeywords) {
      commit('setSearchKeywords', searchKeywords);
      debounce(dispatch, ['findContacts']);
    },

    updateHeaders({ commit, dispatch }, newHeaders) {
      const headers = newHeaders.map(header => Object.assign({}, header));
      dispatch(
        'userPreferences/saveUserPreferences',
        { key: PreferenceKeys.ContactsHeaders, value: headers },
        { root: true },
      );
      localStorage.setItem(PreferenceKeys.ContactsHeaders, JSON.stringify(headers));
      commit('updateHeaders', headers);
    },

    getHeaders({ commit, rootState, dispatch }, customFields) {
      // populate custom header fields
      const customFieldHeaders = customFields.map(customField => {
        return {
          text: customField.name,
          value: `custom_fields_${customField.id}`,
          visible: false,
          sortable: false,
        };
      });
      const allHeaders = contactTableHeaders.concat(customFieldHeaders);

      const dbHeaders = rootState.userPreferences[PreferenceKeys.ContactsHeaders].map(header => {
        return Object.assign({}, header);
      });
      const filteredDbHeaders = loadHeaders(dbHeaders, allHeaders);

      let storedHeaders = loadFromLocalStorage(PreferenceKeys.ContactsHeaders, allHeaders);
      if (filteredDbHeaders.length > 0 && !isEqual(filteredDbHeaders, storedHeaders)) {
        storedHeaders = filteredDbHeaders;
      }

      const headers = JSON.parse(JSON.stringify(storedHeaders)) || [];
      const headerSorted = headers.find(header => header.sorted && header.sortDirection);
      if (headerSorted) {
        commit('pagination/setSortField', headerSorted.value);
        dispatch('pagination/setSortDirection', headerSorted.sortDirection);
      } else {
        dispatch('findContacts', false);
      }
      commit('updateHeaders', headers);
    },

    formatContactData({ commit, getters, state, rootState }, payload) {
      const contacts = payload.map(item => {
        let status = ES_STATUS.pending;
        if (item.opted_out_users && item.opted_out_users.includes(rootState.userInformation.user.id)) {
          status = ES_STATUS.denied;
        } else if (item.opted_in_users && item.opted_in_users.includes(rootState.userInformation.user.id)) {
          status = ES_STATUS.approved;
        }
        // construct additional fields
        const constructedNames = {
          name: {
            value: `${item.first_name || ''} ${item.last_name || ''}`,
            id: item.id,
            owner_user_id: item.owner_user_id,
            path: '',
          },
          owner_name: `${item.owner_first_name || ''}  ${item.owner_last_name || ''}`,
          assigned_name: `${item.assigned_user_first_name || ''} ${item.assigned_user_last_name || ''
            }`,
          phone_mobile: {
            value: item.phone_mobile,
            status: status,
            id: item.id,
            type: 'Mobile phone',
          },
          phone_office: {
            value: item.phone_office,
            id: item.id,
            type: 'Office phone',
          },
          phone_home: {
            value: item.phone_home,
            id: item.id,
            type: 'Home phone',
          },
          Campaign_action: {
            value: getNextCampaignActionDate(
              item.next_ap_task_regular_at,
              item.next_ap_task_recurring_at,
            ),
            id: item.id,
            type: 'date',
          },
          total_campaign_action_tasks: parseInt(
            item.total_ap_tasks_regular + item.total_ap_tasks_recurring,
          ),
        };

        Object.assign(item, constructedNames);

        // format hotness information
        const { hotness, hotness_color: color } = item;
        if (hotness && color) {
          item.hotness = {
            name: hotness,
            color,
          };
        }
        return item;
      });
      commit('setContacts', contacts);
      commit('setIsLoading', false);
    },

    /**
     * use the contacts endpoint for certain bulk actions features because the cached
     * contacts table doesn't have all the information that the contacts table does
     */
    async findSelectedContacts({ state, commit }, queryParams) {
      try {
        const response = await feathers.service('contacts/search').create(
          {
            query: {
              ...queryParams,
              $limit: queryParams.$limit ? queryParams.$limit : DEFAULT_LIMIT_CONTACTS,
              $teams: state.shouldShowTeamMembersContacts,
            },
          },
          {
            headers: ESSearchHeader,
          },
        );
        return response.data;
      } catch (error) {
        // Override the default feathers 400 response which is useful for devs,
        // but not for the general public.
        // TODO MW - move this to someplace general.
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
    },

    async findSelectedCachedContacts({ state, commit }, queryParams) {
      try {
        const response = await feathers.service('cached-contacts/search').create(
          {
            query: {
              ...queryParams,
              $limit: DEFAULT_LIMIT_CONTACTS,
              $teams: state.shouldShowTeamMembersContacts,
            },
          },
          { headers: ESSearchHeader },
        );

        return response.data;
      } catch (error) {
        // Override the default feathers 400 response which is useful for devs,
        // but not for the general public.
        // TODO MW - move this to someplace general.
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
    },

    async findContacts({ commit, getters, dispatch, state, rootState }, forceLoad = true) {
      if (forceLoad === false && state.contacts.length && state.shouldReloadContactsTable === false)
        return;
      commit('setShouldReloadContactsTable', false);
      let response;
      const query = { ...getters.queryParams };
      if (state.contactFilters.applyContactsSlippingAwayConditions) {
        query.$and = [
          ...(query.$and || []),
          ...generateSlippingAwayConditions(rootState.contactFilters.slippingAwayDays),
        ];
      }

      if (query.$keywords && query.$keywords.length > 0) {
        delete query.$sort;
      }

      try {
        commit('setIsLoading', true);
        response = await feathers
          .service('cached-contacts/search')
          .create({ query }, { headers: ESSearchHeader });

        dispatch('formatContactData', response.data);
        commit('pagination/setTotal', response.total);
      } catch (error) {
        // Override the default feathers 400 response which is useful for devs,
        // but not for the general public.
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
      commit('setIsLoading', false);
    },

    async findAllBulkContacts({ state, commit, getters }, options = {}) {
      let { extraQuery, extraFilters } = options;
      const { excludedContactsIds } = state;
      const queryParams = getters.queryParamsForAllContacts;
      let query = Object.assign({}, queryParams);
      if (extraQuery) {
        query = Object.assign(query, extraQuery);
      }
      if (extraFilters) {
        if (query.$and && Array.isArray(query.$and)) {
          query.$and = [...query.$and, ...extraFilters];
        } else {
          query.$and = extraFilters;
        }
      }
      if (excludedContactsIds.length > 0) {
        query.excluded_ids = { $ne: [...excludedContactsIds] };
      }
      delete query.$paginate;

      try {
        commit('setIsLoading', true);

        const allContacts = await feathers
          .service('cached-contacts/search')
          .create({ query }, { headers: ESSearchHeader });

        commit('setAllBulkContacts', allContacts);
      } catch (error) {
        // Override the default feathers 400 response which is useful for devs,
        // but not for the general public.
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
      commit('setIsLoading', false);
    },
    // We'll just comment it out for now
    // async rebuildCacheContacts({ commit }, contactIds) {
    //   try {
    //     await feathers.service('cached-contacts')
    //       .patch(null, { contact_ids: contactIds });
    //   } catch (error) {
    //     if (error.code === 400) {
    //       error.message = customErrors[error.code];
    //     }
    //     commit('setContactTableError', error.message);
    //   }
    // },
    async findAllBulkContactsFromStaticList({ state, commit, getters }) {
      const query = getters.queryParamsForAllContacts;
      try {
        commit('setIsLoading', true);
        const allContacts = await feathers
          .service(`static-lists/${state.selectedEmailListId}/contacts`)
          .find({ query });
        if (allContacts && allContacts.length > 0) {
          commit('setHasEmailLists', true);
        }
        commit('setAllBulkContacts', allContacts);
      } catch (error) {
        // Override the default feathers 400 response which is useful for devs,
        // but not for the general public.
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
      commit('setIsLoading', false);
    },

    resetAllBulkContacts({ commit }) {
      commit('resetAllBulkContacts');
    },

    async findCustomFields({ state, commit, dispatch }) {
      try {
        feathers
          .service('custom-fields')
          .find({
            query: { $paginate: false },
          })
          .then(async response => {
            await dispatch('getHeaders', response.data);
            await commit('resetCustomFields', state);
            commit('setCustomFieldsData', response.data);
          });
      } catch (error) {
        commit('setContactTableError', error.message);
      }
    },
    clearContactTableMessage({ commit }) {
      commit('setContactTableError', null);
    },
    setSortField({ state, commit, dispatch }, sortField) {
      const oldSortField = state.pagination.sortField;
      commit('pagination/setSortField', sortField);
      // Search in the current headers for the actual sortField and change it
      const newHeaders = state.headers.map(header => {
        const newHeader = { ...header };
        if (newHeader.value === oldSortField) {
          newHeader.sorted = false;
        }
        if (newHeader.value === sortField) {
          newHeader.sorted = true;
          newHeader.sortDirection = state.pagination.sortDirection;
        }
        return newHeader;
      });
      dispatch('updateHeaders', newHeaders);
      if (state.selectedEmailListId) {
        dispatch('findEmailListContacts');
      } else {
        dispatch('findContacts');
      }
    },
    async findContact({ commit }, id) {
      try {
        const response = await feathers.service('contacts').get(id);
        return response;
      } catch (error) {
        commit('setContactTableError', error.message);
      }
    },
    async loadEmailLists({ state, dispatch }) {
      if (state.emailLists.length) return;
      await dispatch('findEmailLists');
    },
    async findEmailLists({ state, commit }, offset = 0) {
      try {
        commit('setIsStaticListLoading', true);
        const response = await feathers.service('static-lists').find({
          query: {
            $limit: DEFAULT_LIMIT_EMAIL_LIST,
            $skip: offset,
          },
        });
        const userEmailLists = response.data.map(list => {
          list.is_email_list = true;
          list.name = list.list_name;
          list.list_name = null;
          return list;
        });
        if (userEmailLists && userEmailLists.length > 0) {
          commit('setHasEmailLists', true);
        }
        if (offset === 0) {
          commit('setEmailLists', userEmailLists);
        } else {
          commit('appendEmailLists', userEmailLists);
        }
        commit('setHasMoreEmailListsToLoad', response.total > state.emailLists.length);
      } catch (error) {
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
      } finally {
        commit('setIsStaticListLoading', false);
      }
    },
    async findEmailListContacts({ state, commit, dispatch, getters }) {
      try {
        const query = { ...getters.queryParams };
        commit('setIsLoading', true);
        if (query.$and) delete query.$and;
        const response = await feathers
          .service(`static-lists/${state.selectedEmailListId}/contacts`)
          .find({ query });
        dispatch('formatContactData', response.data);
        commit('pagination/setTotal', response.total);
      } catch (error) {
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
      commit('setIsLoading', false);
    },
    async setLimit({ state, commit, dispatch }, limit) {
      commit('pagination/setLimit', limit);
      if (state.selectedEmailListId) {
        dispatch('findEmailListContacts');
      } else {
        dispatch('findContacts');
      }
    },
    async setPage({ state, commit, dispatch, rootState }, page) {
      commit('pagination/setPage', page);
      commit('setPageLimitMessage', null);
      const { user } = rootState.userInformation;
      if (!user) return;
      const includesContactMS = userHasContactsMSFlag(user);
      if (includesContactMS && page === MAX_ES_SEARCH_PAGES_LIMIT) {
        commit(
          'setPageLimitMessage',
          `More pages found, but we limit its number to ${MAX_ES_SEARCH_PAGES_LIMIT}. Please, refine your search for better results.`,
        );
      }
      if (state.selectedEmailListId) {
        dispatch('findEmailListContacts');
      } else {
        dispatch('findContacts');
      }
    },
    async setLimitAndPage({ state, commit, dispatch, rootState }, payload) {
      commit('pagination/setPage', payload.page < MAX_ES_SEARCH_PAGES_LIMIT ? payload.page : MAX_ES_SEARCH_PAGES_LIMIT);
      commit('pagination/setLimit', payload.limit);
      commit('setPageLimitMessage', null);
      const { user } = rootState.userInformation;
      if (!user) return;
      const includesContactMS = userHasContactsMSFlag(user);
      if (includesContactMS && payload.page >= MAX_ES_SEARCH_PAGES_LIMIT) {
        commit(
          'setPageLimitMessage',
          `More pages found, but we limit its number to ${MAX_ES_SEARCH_PAGES_LIMIT}. Please, refine your search for better results.`,
        );
      }
      if (state.selectedEmailListId) {
        dispatch('findEmailListContacts');
      } else {
        dispatch('findContacts');
      }
    },
    async createEmailList({ state, commit, dispatch }, { name, createCustomAudience }) {
      const contactList = await feathers.service('static-lists').create({
        list_name: name,
      });

      await dispatch('findAllBulkContacts');
      const contactsIds = state.allBulkContacts.map(contact => contact.id);
      commit('resetAllBulkContacts');
      if (contactsIds.length) {
        const payload = {
          listId: contactList.id,
          contactsIds,
        };
        await dispatch('bulkActions/addContactsToStaticList', payload);
      }
      if (createCustomAudience) {
        dispatch('createCustomAudienceFromStaticList', contactList.id);
      }
      return contactList;
    },
    async createCustomAudienceFromStaticList({ dispatch, commit }, emailListId) {
      commit('setIsLoading', true);
      try {
        const customAudience = await feathers.service('/custom-audiences').create({
          static_list_id: emailListId,
        });
        commit('setIsCustomAudienceCreated', true);
        dispatch('findEmailLists');
        commit('setIsLoading', false);
        return customAudience;
      } catch (error) {
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
        commit('setIsLoading', false);
      }
    },
    async deleteEmailList({ commit, dispatch }, staticListId) {
      try {
        await feathers.service('static-lists').remove(staticListId);
        commit('setSuccessfulDelete', true);
        commit('setSuccessMessage', 'Successfully deleted static list');
        await dispatch('findEmailLists');
      } catch (error) {
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setContactTableError', error.message);
      }
    },
    async startStormDialer(
      { commit, dispatch, rootState, state, getters },
      { selectedIds, router },
    ) {
      await dispatch('userInformation/getUser', null, { root: true })
      if (rootState.userInformation &&
        rootState.userInformation.user && 
        rootState.userInformation.user.blocked_from_sending.dialer) {
        const errorMessage = 'Your dialer access has been suspended due to unusual activity or configuration. Please contact support to re-enable your access.';
        return commit('setContactTableError', errorMessage);
      }
      if (!selectedIds.length) {
        const errorMessage = 'You must select at least one contact to use Power Dialer';
        return commit('setContactTableError', errorMessage);
      }
      dispatch('ldAppLayout/setNavBarExpanded', false, { root: true });
      dispatch(
        'preferences/updatePreference',
        { key: 'navBarExpanded', value: false },
        { root: true },
      );
      commit('setIsLoading', true);
      if (!state.stormDialerToken) {
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_AUTHENTICATION_API_BASE_URL}/dialer-token`,
          );
          commit('setStormDialerToken', response.data.token);
          await loadStormScript();
          await window.WavvDialer.init({ token: state.stormDialerToken });
        } catch (err) {
          commit('setIsLoading', false);
          return commit('setContactTableError', err.data.error);
        }
      }
      const sortQueryParams = getters['pagination/sortQueryParams'];
      const selectedContacts = await dispatch('findSelectedCachedContacts', {
        id: { $in: selectedIds },
        ...sortQueryParams,
      });

      let getPhoneNumbers = contact => {
        let numbers = [
          formatPhoneNumberOnlyDigits(contact.phone_mobile),
          formatPhoneNumberOnlyDigits(contact.phone_home),
          formatPhoneNumberOnlyDigits(contact.phone_office),
        ];

        return numbers.filter(n => n !== '');
      };

      const parsedContacts = selectedContacts.map(selectedContact => {
        return {
          contactId: `${selectedContact.id}`,
          numbers: getPhoneNumbers(selectedContact),
          name: `${selectedContact.first_name} ${selectedContact.last_name}`,
        };
      });
      window.WavvDialer.setTheme({ theme: 'DARK' });
      try {
        await window.WavvDialer.startCampaign({ contacts: parsedContacts });
      } catch (err) {
        commit('setIsLoading', false);
        console.log(`error on window.WavvDialer.startCampaign`);
        console.log(`parsedContacts: ${JSON.stringify(parsedContacts)}`);
        return commit('setContactTableError', err);
      }
      commit('setIsLoading', false);

      window.WavvDialer.addLinesChangedListener(({ lines }) => {
        console.log(`Storm.listener.onLinesChanged: ${JSON.stringify(lines)}`);
        lines.forEach(call => {
          if (call.focused) {
            router.push(`/contacts/${call.contactId}`);
          }
        });
      });
      window.WavvDialer.addWaitingForContinueListener(({ waiting, number, contactId }) => {
        console.log(
          `Storm.listener.onWaitingForContinue: waiting: ${waiting}, number: ${number}, contactId: ${contactId}`,
        );
        if (waiting) {
          window.WavvDialer.continueCampaign({
            resume: true,
          });
        }
      });
      window.WavvDialer.addDialerVisibleListener(({ visible }) => {
        commit('dialerVisible', visible);
      });
      window.WavvDialer.addManageSubscriptionListener(() => {
        commit('payment/setShowPlans', true, { root: true });
      });
      window.WavvDialer.addUpsellClickListener(() => {
        commit('payment/setShowPlans', true, { root: true });
      });
      window.WavvDialer.addCallRecordedListener(({ contactId, recordingId }) => {
        dispatch('dialer/saveRecording', { contactId, recordingId }, { root: true });
      });
      window.WavvDialer.addCampaignEndedListener(() => {
        clearInterval(getUserPoll);
      });
      window.WavvDialer.addDialerVisibleListener(({ visible }) => {
        if (!visible) {
          clearInterval(getUserPoll);
        } else {
          getUserPoll = setInterval(async () => {
            await dispatch('userInformation/getUser', null, { root: true })
            if (rootState.userInformation.user.blocked_from_sending.dialer) {
              await window.WavvDialer.close()
              const errorMessage = 'Your dialer access has been suspended due to unusual activity or configuration. Please contact support to re-enable your access.';
              return commit('setContactTableError', errorMessage);
            };
          }, 5 * 60 * 1000)
        }
      })
      window.WavvDialer.addOverlayVisibleListener(({ visible }) => {
        if (!visible) {
          clearInterval(getUserPoll);
        }
      });

    },
    async searchVisibleContacts({ state, commit, getters }) {
      const { selectedEmailListId, excludedContactsIds } = state;
      let query = getters.queryParamsForAllContacts;
      query.$limit = 6;
      if (selectedEmailListId != null) {
        query.contact_list_ids = { $eq: selectedEmailListId };
      }
      if (excludedContactsIds.length > 0) {
        query.excluded_ids = { $ne: [...excludedContactsIds] };
      }

      try {
        const response = await feathers
          .service('cached-contacts/search')
          .create({ query }, { headers: ESSearchHeader });
        commit('setVisibleBulkContacts', response.data);
      } catch (error) {
        console.error(error);
      }
    },
    resetContacts({ commit }) {
      commit('setContacts', []);
      commit('pagination/setTotal', 0);
    },
    setShouldReloadContactsTable({ commit }) {
      commit('setShouldReloadContactsTable', true);
    },
    setSelectedActionType({ commit }, type) {
      commit('setSelectedActionType', type);
    },
    appendExcludedContactId({ commit }, excludedContactsIds) {
      commit('appendExcludedContactId', excludedContactsIds);
    },
    removeIdFromExcludedContactsIds({ commit }, id) {
      commit('removeIdFromExcludedContactsIds', id);
    },
    clearExcludedContactsIds({ commit }) {
      commit('clearExcludedContactsIds');
    },
  },
};
